import React, { useState, useEffect } from "react";
import { FaPencilAlt, FaClipboardList, FaRegCheckCircle, FaArrowRight, FaExchangeAlt } from 'react-icons/fa';
import Footer from "../../Components/Footer/Footer";
import bootstrapToTailwind from "../../BoostrapToTailwind";
import tailwindToBootstrap from "../../TailwindToBootstrap";
import Cursor from "../../Components/Cursor/Cursor";
import Navbar from "../../Components/Navbar/Navbar";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";

// ✅ FIXED: Moved window.scrollTo inside useEffect in the main component.
// Calling it at module level causes errors during SSR and runs on every hot-reload.

const convertFullCode = (inputCode, mapping) => {
    const div = document.createElement('div');
    div.innerHTML = inputCode;

    const convertNode = (node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList) {
                const convertedClasses = Array.from(node.classList).map((cls) => {
                    const converted = mapping[cls];
                    return converted || `/* ${cls} not found */`;
                }).join(' ');
                node.className = convertedClasses;
            }
            Array.from(node.childNodes).forEach(convertNode);
        }
    };

    convertNode(div);
    return div.innerHTML;
};

// ✅ NEW: Stats bar — builds trust signals and adds keyword-rich visible text
const StatsBar = () => (
    <div className="bg-teal-700 text-white py-3 px-4">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-6 text-sm font-medium">
            <span>⚡ Instant conversion</span>
            <span>🔄 Bootstrap ↔ Tailwind (both directions)</span>
            <span>✅ Free — no signup</span>
            <span>📋 Copy to clipboard</span>
        </div>
    </div>
);

const HowToUse = () => (
    <>
        {/* How to Use */}
        <div className="bg-gradient-to-r from-blue-50 via-teal-50 to-green-50 py-12 px-6">
            <div className="max-w-4xl mx-auto p-8 bg-white rounded-lg shadow-lg">
                <h2 className="text-4xl font-extrabold text-gray-900 mb-4 text-center">
                    How to Convert Bootstrap to Tailwind CSS
                </h2>
                {/* ✅ ADDED: Target the "css to tailwind converter" zero-click query explicitly */}
                <p className="text-gray-600 mb-2 text-center text-sm">
                    Also works as a <strong>CSS to Tailwind converter</strong>, <strong>Tailwind to Bootstrap converter</strong>, and <strong>HTML to Tailwind converter</strong>.
                </p>
                <p className="text-gray-700 mb-8 text-lg leading-relaxed text-center">
                    Follow these steps to migrate from Bootstrap to Tailwind CSS in under a minute:
                </p>
                <ol className="space-y-8">
                    <li className="flex items-start space-x-4">
                        <div className="flex-shrink-0 text-teal-600">
                            <FaPencilAlt className="h-8 w-8" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Step 1 — Paste your Bootstrap HTML</h3>
                            <p className="text-gray-600">
                                Copy your existing Bootstrap code and paste it into the left input panel. Works with Bootstrap 3, 4, and 5.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start space-x-4">
                        <div className="flex-shrink-0 text-teal-600">
                            <FaExchangeAlt className="h-8 w-8" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Step 2 — Choose direction</h3>
                            <p className="text-gray-600">
                                Select <strong>Bootstrap → Tailwind</strong> or <strong>Tailwind → Bootstrap</strong> using the toggle. Our converter supports both directions.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start space-x-4">
                        <div className="flex-shrink-0 text-teal-600">
                            <FaArrowRight className="h-8 w-8" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Step 3 — Click Convert</h3>
                            <p className="text-gray-600">
                                Hit the <strong>Convert</strong> button. The tool instantly replaces Bootstrap class names with Tailwind CSS equivalents throughout your markup.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start space-x-4">
                        <div className="flex-shrink-0 text-teal-600">
                            <FaClipboardList className="h-8 w-8" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Step 4 — Copy your Tailwind CSS code</h3>
                            <p className="text-gray-600">
                                The converted Tailwind CSS appears in the right panel. Click <strong>Copy Code</strong> to copy it directly to your clipboard.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start space-x-4">
                        <div className="flex-shrink-0 text-teal-600">
                            <FaRegCheckCircle className="h-8 w-8" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Step 5 — Convert again</h3>
                            <p className="text-gray-600">
                                Click <strong>Reset</strong> to clear the fields and start a new Bootstrap to Tailwind conversion.
                            </p>
                        </div>
                    </li>
                </ol>
                <p className="text-gray-700 mt-6 text-center">
                    Need more help? <Link to={'/how'} className="text-teal-600 hover:underline font-semibold">Read our full migration guide</Link> or <Link to={'/contact'} className="text-teal-600 hover:underline font-semibold">contact us</Link>.
                </p>
            </div>
        </div>

        {/* Code Example */}
        <div className="max-w-4xl mx-auto p-8 bg-white rounded-lg shadow-lg mt-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Bootstrap to Tailwind CSS — Live Example</h2>
            <p className="text-gray-600 text-center mb-6 text-sm">See what a typical Bootstrap to Tailwind conversion looks like</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-300">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">Bootstrap Input</h3>
                    <p className="text-xs text-gray-500 mb-3">Bootstrap 4 / 5 classes</p>
                    <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                        <code>{`<div class="container">
  <div class="row">
    <div class="col-md-6">
      <h2>Heading</h2>
      <p>Bootstrap example.</p>
    </div>
    <div class="col-md-6">
      <button class="btn btn-primary">
        Click Me
      </button>
    </div>
  </div>
</div>`}
                        </code>
                    </pre>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg border border-teal-300">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">Tailwind CSS Output</h3>
                    <p className="text-xs text-gray-500 mb-3">Converted Tailwind utility classes</p>
                    <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                        <code>{`<div class="container mx-auto">
  <div class="flex flex-wrap">
    <div class="w-full md:w-1/2 p-4">
      <h2 class="text-2xl font-bold">
        Heading
      </h2>
      <p>Tailwind example.</p>
    </div>
    <div class="w-full md:w-1/2 p-4">
      <button class="bg-blue-500 text-white
        py-2 px-4 rounded">
        Click Me
      </button>
    </div>
  </div>
</div>`}
                        </code>
                    </pre>
                </div>
            </div>
        </div>

        {/* Why Choose + Benefits — targeting "migrate bootstrap to tailwind" and related queries */}
        <div className="max-w-6xl mx-auto p-8 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg shadow-lg mt-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Use This Bootstrap to Tailwind Converter?</h2>
                    <div className="space-y-4">
                        {[
                            { title: "Instant, no-setup conversion", desc: "No npm install, no config files. Paste your Bootstrap HTML and get Tailwind CSS back in milliseconds." },
                            { title: "Bidirectional support", desc: "Convert Bootstrap → Tailwind or Tailwind → Bootstrap. Switch directions with one click." },
                            { title: "Bootstrap 3, 4 & 5 support", desc: "Handles Bootstrap classes across all major versions. Great for legacy migration projects." },
                            { title: "Completely free", desc: "No account, no rate limits, no paywall. Use it as many times as you need." },
                        ].map((item, i) => (
                            <div key={i} className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Benefits of Migrating Bootstrap → Tailwind</h2>
                    <div className="space-y-3">
                        {[
                            { title: "Smaller CSS bundle", desc: "Tailwind purges unused classes at build time. Bootstrap ships ~150KB min; Tailwind production builds are typically under 10KB." },
                            { title: "No specificity wars", desc: "Utility classes don't fight each other. Say goodbye to !important overrides." },
                            { title: "Design system built-in", desc: "Tailwind's spacing, color, and typography scales are consistent by default, unlike Bootstrap overrides." },
                            { title: "Better developer experience", desc: "Write styles directly in your markup. No context switching between HTML and CSS files." },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
                                <h3 className="text-base font-semibold text-gray-900 mb-1">{item.title}</h3>
                                <p className="text-gray-600 text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>

        {/* ✅ NEW: Comparison table — targets "difference between tailwind and bootstrap" + table rich result */}
        <div className="max-w-4xl mx-auto p-8 bg-white rounded-lg shadow-lg mt-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Bootstrap vs Tailwind CSS — Quick Comparison</h2>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="text-left p-3 border border-gray-200 font-semibold text-gray-900">Feature</th>
                            <th className="text-left p-3 border border-gray-200 font-semibold text-purple-700">Bootstrap</th>
                            <th className="text-left p-3 border border-gray-200 font-semibold text-teal-700">Tailwind CSS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            ["Approach", "Component-based", "Utility-first"],
                            ["Bundle size", "~150KB minified", "~5–15KB (purged)"],
                            ["Customization", "Override with Sass/CSS", "Configure tailwind.config.js"],
                            ["Learning curve", "Easier for beginners", "Steeper but more powerful"],
                            ["Pre-built components", "Yes (buttons, cards, modals)", "No (use headless UI or Flowbite)"],
                            ["Design flexibility", "Limited by Bootstrap styles", "Unlimited — build anything"],
                            ["Framework support", "Works anywhere", "Best with React/Vue/Next.js"],
                        ].map(([feature, boot, tail], i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                <td className="p-3 border border-gray-200 font-medium text-gray-800">{feature}</td>
                                <td className="p-3 border border-gray-200 text-gray-600">{boot}</td>
                                <td className="p-3 border border-gray-200 text-gray-600">{tail}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>

        {/* FAQ — targets FAQ rich results in Google */}
        <div className="max-w-4xl mx-auto p-8 bg-white rounded-lg shadow-lg mt-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
                {[
                    {
                        q: "Can I use Tailwind and Bootstrap together?",
                        a: "You can, but it's not recommended. Both frameworks include CSS resets that conflict with each other, and class names can collide. Our converter helps you fully migrate so you can remove Bootstrap entirely."
                    },
                    {
                        q: "How accurate is the Bootstrap to Tailwind conversion?",
                        a: "Very accurate for standard Bootstrap utility, grid, and spacing classes. Complex custom components or Bootstrap JavaScript interactions (modals, dropdowns) need manual handling after conversion."
                    },
                    {
                        q: "Is this converter free?",
                        a: "Yes — completely free. No account required, no conversion limits, no hidden fees. Just paste and convert."
                    },
                    {
                        q: "Can I convert Tailwind CSS back to Bootstrap?",
                        a: "Yes. Use the direction toggle to switch to Tailwind → Bootstrap mode before converting. Our tool supports both directions."
                    },
                    {
                        q: "Does this work as a CSS to Tailwind converter?",
                        a: "Our tool converts Bootstrap class names inside HTML markup to their Tailwind equivalents. If you have raw CSS you want to convert to Tailwind utilities, paste your HTML that uses those Bootstrap classes."
                    },
                    {
                        q: "Can I use this as an HTML to Tailwind converter?",
                        a: "Yes — paste any HTML file that contains Bootstrap class names. The converter processes the entire file and replaces all Bootstrap classes with Tailwind equivalents."
                    },
                    {
                        q: "What Bootstrap versions are supported?",
                        a: "Our converter supports Bootstrap 3, Bootstrap 4, and Bootstrap 5 class naming conventions."
                    },
                    {
                        q: "How do I migrate from Bootstrap to Tailwind CSS?",
                        a: "Paste your Bootstrap HTML into the left panel, select Bootstrap → Tailwind, click Convert, and copy the output. For large projects, convert file by file and test each page."
                    },
                ].map((faq, i) => (
                    <div key={i} className="border-b border-gray-200 pb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.q}</h3>
                        <p className="text-gray-600">{faq.a}</p>
                    </div>
                ))}
            </div>
        </div>
    </>
);

const LandingPage = () => {
    const [inputCode, setInputCode] = useState("");
    const [outputCode, setOutputCode] = useState("");
    const [showOutput, setShowOutput] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [conversionType, setConversionType] = useState('bootstrapToTailwind');
    const [errorMessage, setErrorMessage] = useState("");

    // ✅ FIXED: Moved scroll to top inside useEffect (was running at module level)
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        AOS.init({ offset: 150, duration: 300, easing: "ease-in-sine", delay: 50, disable: 'mobile' });
    }, []);

    const handleConvert = () => {
        if (!inputCode.trim()) {
            setErrorMessage("Please paste some code into the input field first.");
            return;
        }
        setErrorMessage("");
        const mapping = conversionType === "bootstrapToTailwind" ? bootstrapToTailwind : tailwindToBootstrap;
        setOutputCode(convertFullCode(inputCode, mapping));
        setShowOutput(true);
    };

    const handleCopy = () => {
        const codeToCopy = outputCode
            .split("\n")
            .filter((line) => !line.startsWith("/*"))
            .join("\n");
        if (codeToCopy) {
            navigator.clipboard.writeText(codeToCopy);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const handleReset = () => {
        setInputCode("");
        setOutputCode("");
        setShowOutput(false);
        setErrorMessage("");
    };

    const isBootstrapToTailwind = conversionType === 'bootstrapToTailwind';

    return (
        <div className="relative min-h-screen bg-gradient-to-r from-blue-50 to-green-50">
            <Cursor />
            <Navbar />
            <StatsBar />

            <div className="container mx-auto px-4 pt-8">
                {/* Breadcrumb */}
                <nav className="mb-4" aria-label="Breadcrumb">
                    <ol className="flex items-center space-x-2 text-sm text-gray-600">
                        <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
                        <li className="text-gray-400">/</li>
                        <li className="text-gray-900 font-medium">Bootstrap to Tailwind Converter</li>
                    </ol>
                </nav>

                {/* ✅ IMPROVED: H1 targets primary query; subtitle targets secondary zero-click queries */}
                <h1 className="text-4xl font-extrabold text-gray-900 text-center mb-3">
                    Bootstrap to Tailwind Converter
                </h1>
                <p className="text-lg text-gray-700 mb-2 text-center max-w-3xl mx-auto">
                    Free online tool to <strong>convert Bootstrap to Tailwind CSS</strong> — or Tailwind back to Bootstrap. 
                    Paste your HTML, click Convert, copy the result.
                </p>
                {/* ✅ NEW: Secondary keyword line — captures "css to tailwind converter", "html to tailwind", "tailwind to bootstrap" */}
                <p className="text-sm text-gray-500 mb-6 text-center">
                    Works as a <strong>CSS to Tailwind converter</strong> · <strong>HTML to Tailwind converter</strong> · <strong>Tailwind to Bootstrap converter</strong> · Bootstrap 3, 4 &amp; 5 support
                </p>

                {/* ✅ IMPROVED: Conversion direction toggle — visible, labeled, and keyboard accessible */}
                <div className="flex justify-center mb-4">
                    <div className="inline-flex items-center bg-white rounded-lg shadow border border-gray-200 p-1">
                        <button
                            onClick={() => setConversionType('bootstrapToTailwind')}
                            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                                isBootstrapToTailwind
                                    ? 'bg-teal-600 text-white shadow'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            aria-pressed={isBootstrapToTailwind}
                        >
                            Bootstrap → Tailwind
                        </button>
                        <button
                            onClick={() => setConversionType('tailwindToBootstrap')}
                            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                                !isBootstrapToTailwind
                                    ? 'bg-teal-600 text-white shadow'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            aria-pressed={!isBootstrapToTailwind}
                        >
                            Tailwind → Bootstrap
                        </button>
                    </div>
                </div>

                {/* Code editor panels */}
                <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isBootstrapToTailwind ? "Bootstrap HTML (input)" : "Tailwind CSS HTML (input)"}
                        </label>
                        <textarea
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            rows="12"
                            placeholder={isBootstrapToTailwind
                                ? `Paste your Bootstrap HTML here...\n\nExample:\n<div class="container">\n  <button class="btn btn-primary">Click</button>\n</div>`
                                : `Paste your Tailwind HTML here...\n\nExample:\n<div class="container mx-auto">\n  <button class="bg-blue-500 text-white px-4 py-2 rounded">Click</button>\n</div>`
                            }
                            className="w-full p-4 border border-gray-300 rounded-lg shadow-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            aria-label="Input code"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isBootstrapToTailwind ? "Tailwind CSS (output)" : "Bootstrap HTML (output)"}
                        </label>
                        <textarea
                            value={outputCode}
                            readOnly
                            rows="12"
                            placeholder="Converted code will appear here..."
                            className="w-full p-4 border border-gray-300 rounded-lg shadow-sm font-mono text-sm bg-gray-50"
                            aria-label="Output code"
                            aria-live="polite"
                        />
                    </div>
                </div>

                {errorMessage && (
                    <p className="text-red-600 text-center mt-3 text-sm" role="alert">{errorMessage}</p>
                )}

                <div className="flex justify-center mt-5 space-x-4 flex-wrap gap-y-3">
                    <button
                        onClick={handleConvert}
                        className="bg-teal-600 text-white px-8 py-3 rounded-lg shadow-lg hover:bg-teal-700 transition duration-300 font-semibold"
                    >
                        Convert {isBootstrapToTailwind ? "Bootstrap → Tailwind" : "Tailwind → Bootstrap"}
                    </button>
                    {showOutput && (
                        <>
                            <button
                                onClick={handleCopy}
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300 font-semibold"
                            >
                                {isCopied ? '✓ Copied!' : 'Copy Code'}
                            </button>
                            <button
                                onClick={handleReset}
                                className="bg-gray-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-gray-700 transition duration-300"
                            >
                                Reset
                            </button>
                        </>
                    )}
                </div>

                <HowToUse />
            </div>
            <Footer />
        </div>
    );
};

export default LandingPage;